import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Store, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "rounded-xl border border-neutral-700 bg-black px-4 py-3 text-sm text-stone-100 outline-none focus:border-cyan-500";
const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

export default function VendorsPage() {
  const [params, setParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [booths, setBooths] = useState(null);
  const [sel, setSel] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ business_name: "", contact_name: "", email: "", phone: "", vendor_category: "", description: "" });
  const slug = params.get("event") || "";

  useEffect(() => {
    api.get("/events").then((r) => {
      const up = r.data.filter((e) => new Date(e.date) >= Date.now());
      setEvents(up);
      if (!slug && up[0]) setParams({ event: up[0].slug }, { replace: true });
    }).catch(() => {});
    if (params.get("cancelled")) toast("Checkout cancelled — your booth was not reserved.");
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!slug) return;
    setBooths(null); setSel("");
    api.get(`/events/${slug}/booths`).then((r) => setBooths(r.data)).catch(() => setBooths({ booth_types: [] }));
  }, [slug]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const chosen = booths?.booth_types.find((b) => b.id === sel);

  const submit = async (e) => {
    e.preventDefault();
    if (!chosen) return toast.error("Pick a booth type first.");
    setBusy(true);
    try {
      const { data } = await api.post("/vendors/checkout", { ...form, event_slug: slug, booth_type_id: sel, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20" data-testid="vendors-page">
      <span className="eyebrow">Vendor Marketplace</span>
      <h1 className="mt-2 font-display text-4xl uppercase text-chrome sm:text-5xl lg:text-6xl">Sell Where The <span className="text-gold">Crowd Is.</span></h1>
      <p className="mt-4 max-w-2xl text-neutral-300">Reserve a booth at any EON production. Inventory is live — pay online and your space is locked instantly. Load-in details arrive 7 days before the event.</p>

      <div className="mt-10 grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <label className="eyebrow">Choose event</label>
          <select value={slug} onChange={(e) => setParams({ event: e.target.value })} data-testid="vendor-event-select" className={`${inputCls} mt-2 w-full`}>
            {events.map((e) => <option key={e.slug} value={e.slug}>{e.title} — {fmtDate(e.date)}</option>)}
          </select>

          <div className="mt-6 grid gap-4 sm:grid-cols-3" data-testid="booth-types">
            {booths === null && <div className="col-span-full flex items-center gap-2 text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading inventory…</div>}
            {booths?.booth_types.map((b) => {
              const out = b.remaining <= 0;
              return (
                <button key={b.id} type="button" disabled={out} onClick={() => setSel(b.id)} data-testid={`booth-type-${b.id}`}
                  className={`glass relative flex flex-col rounded-2xl p-5 text-left transition-all hover:-translate-y-0.5 disabled:opacity-40 ${sel === b.id ? "ring-gold border-cyan-500/60" : ""}`}>
                  {sel === b.id && <Check className="absolute right-4 top-4 h-5 w-5 text-cyan-400" />}
                  <Store className="h-6 w-6 text-cyan-400" />
                  <div className="mt-3 font-cond text-lg font-extrabold uppercase leading-tight text-white">{b.name}</div>
                  <div className="mt-1 flex-1 text-xs text-neutral-400">{b.description}</div>
                  <div className="mt-4 font-display text-3xl text-gold">${b.price.toLocaleString()}</div>
                  <div className={`mt-1 font-mono-label text-[10px] uppercase tracking-widest ${out ? "text-red-400" : b.remaining <= 3 ? "text-cyan-300" : "text-neutral-500"}`} data-testid={`booth-remaining-${b.id}`}>
                    {out ? "Sold out" : `${b.remaining} of ${b.quantity} left`}
                  </div>
                </button>
              );
            })}
            {booths && !booths.booth_types.length && <p className="col-span-full text-neutral-500" data-testid="no-booths">No vendor booths for this event yet.</p>}
          </div>
        </div>

        <form onSubmit={submit} className="glass rounded-3xl p-6 lg:col-span-5" data-testid="vendor-form">
          <span className="eyebrow">Vendor details</span>
          <h2 className="mt-2 font-display text-2xl uppercase text-chrome">{chosen ? chosen.name : "Select a booth"}</h2>
          <div className="mt-5 grid gap-3">
            <input required placeholder="Business name" value={form.business_name} onChange={set("business_name")} data-testid="vendor-business" className={inputCls} />
            <input required placeholder="Contact name" value={form.contact_name} onChange={set("contact_name")} data-testid="vendor-contact" className={inputCls} />
            <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="vendor-email" className={inputCls} />
            <input placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="vendor-phone" className={inputCls} />
            <select value={form.vendor_category} onChange={set("vendor_category")} data-testid="vendor-category" className={inputCls}>
              <option value="">What do you sell?</option>
              {["Food & Beverage", "Apparel & Boots", "Tack & Equestrian", "Art & Crafts", "Beauty & Wellness", "Services", "Other"].map((c) => <option key={c}>{c}</option>)}
            </select>
            <textarea rows={3} placeholder="Tell us about your products" value={form.description} onChange={set("description")} data-testid="vendor-description" className={inputCls} />
          </div>
          <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-4">
            <span className="text-sm text-neutral-400">Due today</span>
            <span className="font-display text-2xl text-gold" data-testid="vendor-total">${chosen ? chosen.price.toLocaleString() : "—"}</span>
          </div>
          <button type="submit" disabled={busy || !chosen} data-testid="vendor-pay-btn" className="btn-gold mt-4 flex w-full items-center justify-center gap-2 rounded-full py-4 font-display text-lg uppercase disabled:opacity-50">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Reserve & Pay"}
          </button>
          <p className="mt-3 text-center font-mono-label text-[10px] uppercase tracking-widest text-stone-600">Secure checkout by Stripe</p>
        </form>
      </div>
    </div>
  );
}
